import { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { Users, TrendingUp, TrendingDown, Minus, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useOrgContext } from '../state/OrgContext'
import {
  teacherAnalytics,
  teachers,
  classTrendData,
  studentTrendData,
  studentTopicPerformance,
} from '../data/mockData'

// ── SVG Helpers ──────────────────────────────────────────────────

type Difficulty = 'easy' | 'medium' | 'hard'

const DIFFICULTY_COLOR: Record<Difficulty, string> = {
  easy: 'var(--color-success)',
  medium: 'var(--accent-copper)',
  hard: 'var(--color-danger)',
}

/** Maps backend Turkish difficulty strings to internal Difficulty keys */
const toDiffKey = (d: string): Difficulty =>
  d === 'Kolay' ? 'easy' : d === 'Orta' ? 'medium' : 'hard'

function DifficultyBadge({ level }: { level: Difficulty }) {
  const { t } = useTranslation('pages')
  const cls =
    level === 'easy'
      ? 'badge-success'
      : level === 'hard'
        ? 'badge-danger'
        : 'badge-neutral'
  return <span className={`badge ${cls}`}>{t(`analytics.difficulty.${level}`)}</span>
}

function TrendLine({
  data,
  overlayData,
  difficulties,
  width = 520,
  height = 200,
}: {
  data: number[]
  overlayData?: number[]
  difficulties?: Difficulty[]
  width?: number
  height?: number
}) {
  if (data.length < 2) return null

  const pad = { top: 20, right: 16, bottom: 28, left: 38 }
  const w = width - pad.left - pad.right
  const h = height - pad.top - pad.bottom

  const allValues = [...data, ...(overlayData || [])]
  const min = Math.min(...allValues) - 5
  const max = Math.max(...allValues) + 5
  const range = max - min || 1

  const toX = (i: number) => pad.left + (i / (data.length - 1)) * w
  const toY = (v: number) => pad.top + h - ((v - min) / range) * h

  const linePath = data
    .map((v, i) => `${i === 0 ? 'M' : 'L'}${toX(i)},${toY(v)}`)
    .join(' ')

  const gridCount = 4
  const gridLines = Array.from({ length: gridCount + 1 }, (_, i) => {
    const val = min + (range / gridCount) * i
    return { y: toY(val), label: Math.round(val) }
  })

  const overlayPath = overlayData
    ? overlayData
        .map((v, i) => `${i === 0 ? 'M' : 'L'}${toX(i)},${toY(v)}`)
        .join(' ')
    : null

  return (
    <svg width={width} height={height} className="analytics-chart-svg">
      {gridLines.map((g, i) => (
        <g key={i}>
          <line
            x1={pad.left}
            y1={g.y}
            x2={width - pad.right}
            y2={g.y}
            stroke="var(--border-light)"
            strokeWidth={1}
          />
          <text
            x={pad.left - 6}
            y={g.y + 4}
            textAnchor="end"
            className="analytics-chart-label"
          >
            {g.label}%
          </text>
        </g>
      ))}

      {overlayPath && (
        <path
          d={overlayPath}
          fill="none"
          stroke="var(--border-color)"
          strokeWidth={1.5}
          strokeDasharray="5,4"
        />
      )}

      <path
        d={linePath}
        fill="none"
        stroke="var(--accent-copper)"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {data.map((v, i) => {
        const diff = difficulties?.[i]
        const stroke = diff ? DIFFICULTY_COLOR[diff] : 'var(--accent-copper)'
        const fill = diff ? DIFFICULTY_COLOR[diff] : 'var(--bg-primary)'
        const r = difficulties ? 5 : 3.5
        return (
          <circle
            key={i}
            cx={toX(i)}
            cy={toY(v)}
            r={r}
            fill={fill}
            stroke={stroke}
            strokeWidth={2}
          />
        )
      })}
    </svg>
  )
}

function HorizontalBar({ label, pct }: { label: string; pct: number }) {
  const color =
    pct >= 80
      ? 'var(--color-success)'
      : pct >= 60
        ? 'var(--accent-copper)'
        : 'var(--color-danger)'
  return (
    <div className="analytics-hbar-row">
      <span className="analytics-hbar-label">{label}</span>
      <div className="analytics-hbar-track">
        <div
          className="analytics-hbar-fill"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
      <span className="analytics-hbar-pct">{pct}%</span>
    </div>
  )
}

function ScoreBar({ score, max = 100 }: { score: number; max?: number }) {
  const pct = Math.round((score / max) * 100)
  return (
    <div className="analytics-score-bar">
      <div className="analytics-score-bar-track">
        <div className="analytics-score-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="analytics-score-bar-val">{score.toFixed(1)}%</span>
    </div>
  )
}

function TrendBadge({ data }: { data: number[] }) {
  const { t } = useTranslation('pages')
  if (data.length < 2) return null
  const diff = data[data.length - 1] - data[0]
  if (diff > 3)
    return (
      <span className="badge badge-success" style={{ gap: 3 }}>
        <TrendingUp size={12} />+{diff.toFixed(0)}%
      </span>
    )
  if (diff < -3)
    return (
      <span className="badge badge-danger" style={{ gap: 3 }}>
        <TrendingDown size={12} />
        {diff.toFixed(0)}%
      </span>
    )
  return (
    <span className="badge badge-neutral" style={{ gap: 3 }}>
      <Minus size={12} />
      {t('analytics.stableLabel')}
    </span>
  )
}

// ── Main Component ──────────────────────────────────────────────

export default function Analytics() {
  const { activeRole } = useOrgContext()
  const { t } = useTranslation('pages')

  if (activeRole === 'TEACHER') return <Navigate to="/exams" replace />

  const [selectedTeacherId, setSelectedTeacherId] = useState<number | null>(null)
  const [selectedClass, setSelectedClass] = useState<string | null>(null)
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null)

  const selectedTeacher =
    teacherAnalytics.find((t) => t.teacherId === selectedTeacherId) ?? null
  const selectedTeacherInfo =
    teachers.find((t) => t.id === selectedTeacherId) ?? null
  const selectedTeacherName = selectedTeacherInfo?.name ?? '\u2014'

  const availableClasses = selectedTeacher?.classes ?? []

  const activeClass =
    selectedClass && availableClasses.includes(selectedClass)
      ? selectedClass
      : availableClasses[0] ?? null

  const activeSubject = selectedTeacherInfo?.subject ?? null

  const teacherClassKey =
    selectedTeacher && activeClass
      ? `${selectedTeacher.teacherId}|${activeClass}`
      : null
  const classTrend = teacherClassKey
    ? (classTrendData[teacherClassKey] ?? [])
    : []
  const classAvgs = classTrend.map((d) => d.avg)

  const studentsInSubject = activeSubject
    ? (studentTrendData[activeSubject] ?? [])
    : []
  const currentStudent =
    studentsInSubject.find((s) => s.studentId === selectedStudentId) ??
    studentsInSubject[0] ??
    null

  const topicData =
    currentStudent && activeSubject && studentTopicPerformance[activeSubject]
      ? (studentTopicPerformance[activeSubject][currentStudent.studentId] ?? [])
      : []

  function handleTeacherClick(teacherId: number) {
    if (selectedTeacherId === teacherId) {
      setSelectedTeacherId(null)
      setSelectedClass(null)
      setSelectedStudentId(null)
    } else {
      const entry = teacherAnalytics.find((t) => t.teacherId === teacherId)
      setSelectedTeacherId(teacherId)
      setSelectedClass(entry?.classes[0] ?? null)
      setSelectedStudentId(null)
    }
  }

  function handleClassChange(className: string) {
    setSelectedClass(className)
    setSelectedStudentId(null)
  }

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <h2 className="page-header-title">{t('analytics.title')}</h2>
        <p className="page-header-sub">
          {t('analytics.subtitle')}
        </p>
      </div>

      {/* ── Section A: Teacher Comparison ───────────── */}
      <section className="analytics-section">
        <div className="flex items-center gap-8 mb-16">
          <Users size={18} style={{ color: 'var(--accent-copper)' }} />
          <h3 className="card-title">{t('analytics.teacherComparison')}</h3>
        </div>

        <div className="card">
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('analytics.teacherCol')}</th>
                  <th>{t('analytics.subjectCol')}</th>
                  <th>{t('analytics.classesCol')}</th>
                  <th style={{ textAlign: 'right' }}>{t('analytics.examsCol')}</th>
                  <th style={{ minWidth: 200 }}>{t('analytics.avgScoreCol')}</th>
                  <th>{t('analytics.strongTopicsCol')}</th>
                  <th>{t('analytics.weakTopicsCol')}</th>
                </tr>
              </thead>
              <tbody>
                {teacherAnalytics.map((t) => {
                  const isSelected = selectedTeacherId === t.teacherId
                  const info = teachers.find((x) => x.id === t.teacherId)
                  return (
                    <tr
                      key={t.teacherId}
                      onClick={() => handleTeacherClick(t.teacherId)}
                      style={{
                        cursor: 'pointer',
                        background: isSelected
                          ? 'var(--bg-accent-subtle, rgba(196,149,106,0.07))'
                          : undefined,
                        borderLeft: isSelected
                          ? '3px solid var(--accent-copper)'
                          : '3px solid transparent',
                        transition: 'background 0.15s, border-color 0.15s',
                      }}
                      className="analytics-teacher-row"
                    >
                      <td>
                        <div className="flex items-center gap-8">
                          <span className="font-semibold">
                            {info?.name ?? '\u2014'}
                          </span>
                          {isSelected && (
                            <ChevronRight
                              size={14}
                              style={{ color: 'var(--accent-copper)' }}
                            />
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="text-sm">{info?.subject ?? '\u2014'}</span>
                      </td>
                      <td>
                        <div className="flex flex-wrap gap-4">
                          {t.classes.map((c) => (
                            <span
                              key={c}
                              className="chip chip-neutral"
                              style={{ fontSize: '0.7rem' }}
                            >
                              {c}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <span className="font-heading font-semibold">
                          {t.examsGraded}
                        </span>
                      </td>
                      <td>
                        <ScoreBar score={t.avgClassScore} />
                      </td>
                      <td>
                        <div className="flex flex-wrap gap-4">
                          {t.strongTopics.map((s) => (
                            <span
                              key={s}
                              className="chip chip-check"
                              style={{ fontSize: '0.7rem' }}
                            >
                              {s}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>
                        <div className="flex flex-wrap gap-4">
                          {t.weakTopics.map((w) => (
                            <span
                              key={w}
                              className="chip chip-missing"
                              style={{ fontSize: '0.7rem' }}
                            >
                              {w}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        {!selectedTeacher && (
          <p
            className="text-sm text-muted mt-12"
            style={{ paddingLeft: 4 }}
          >
            {t('analytics.selectTeacherHint')}
          </p>
        )}
      </section>

      {/* ── Only show the rest when a teacher is selected ── */}
      {selectedTeacher && (
        <>
          {/* ── Class selector ── */}
          <div className="analytics-class-selector">
            <span className="text-sm text-muted">
              <strong>{selectedTeacherName}</strong>{' '}
              {t('analytics.viewingFor')}
              {activeSubject && (
                <>
                  {' '}
                  \u2014{' '}
                  <span style={{ color: 'var(--accent-copper)' }}>
                    {activeSubject}
                  </span>
                </>
              )}
            </span>
            {availableClasses.length > 1 && (
              <div
                className="flex items-center gap-8"
                style={{ marginLeft: 'auto' }}
              >
                <label
                  className="form-label"
                  style={{ marginBottom: 0, whiteSpace: 'nowrap' }}
                >
                  {t('analytics.classLabel')}
                </label>
                <select
                  className="form-select"
                  value={activeClass ?? ''}
                  onChange={(e) => handleClassChange(e.target.value)}
                  style={{ minWidth: 200 }}
                >
                  {availableClasses.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {availableClasses.length === 1 && (
              <span
                className="chip chip-neutral"
                style={{ marginLeft: 'auto' }}
              >
                {activeClass}
              </span>
            )}
          </div>

          {/* ── Section B: Class Trend ── */}
          <section className="analytics-section">
            <div className="flex items-center gap-8 mb-4">
              <h3 className="card-title">{t('analytics.classTrend')}</h3>
              <TrendBadge data={classAvgs} />
            </div>
            <p className="text-sm text-muted mb-16">
              {t('analytics.classTrendSubtitle')} {activeClass}
              {activeSubject ? ` \u00b7 ${activeSubject}` : ''}
            </p>

            <div className="card card-padding">
              {classTrend.length >= 2 ? (
                <>
                  <div className="analytics-chart-container">
                    <TrendLine
                      data={classAvgs}
                      difficulties={classTrend.map((d) => toDiffKey(d.difficulty))}
                      width={560}
                      height={210}
                    />
                    <div className="analytics-chart-labels">
                      {classTrend.map((d, i) => (
                        <span key={i} className="analytics-chart-xlabel">
                          {d.exam.length > 20
                            ? d.exam.slice(0, 18) + '\u2026'
                            : d.exam}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Difficulty legend */}
                  <div className="flex items-center gap-16 mt-12">
                    {(['easy', 'medium', 'hard'] as Difficulty[]).map((level) => (
                      <span key={level} className="analytics-legend">
                        <span
                          className="analytics-legend-dot"
                          style={{ background: DIFFICULTY_COLOR[level] }}
                        />
                        {t(`analytics.difficulty.${level}`)}
                      </span>
                    ))}
                  </div>

                  <div className="table-wrapper mt-16">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>{t('analytics.examCol')}</th>
                          <th>{t('analytics.dateCol')}</th>
                          <th>{t('analytics.difficultyCol')}</th>
                          <th style={{ textAlign: 'right' }}>{t('analytics.avgClassScore')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {classTrend.map((d, i) => (
                          <tr key={i}>
                            <td className="text-sm font-semibold">{d.exam}</td>
                            <td className="text-sm text-muted">{d.date}</td>
                            <td>
                              <DifficultyBadge level={toDiffKey(d.difficulty)} />
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <span className="font-heading font-semibold">
                                {d.avg}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted">
                  {t('analytics.noExamData')}
                </p>
              )}
            </div>
          </section>

          {/* ── Section C + D: Student Performance ── */}
          <section className="analytics-section">
            <div className="flex items-center gap-8 mb-4">
              <h3 className="card-title">{t('analytics.studentPerformance')}</h3>
            </div>
            <p className="text-sm text-muted mb-16">
              {t('analytics.studentPerfSubtitle')} {activeClass}
              {activeSubject ? ` \u00b7 ${activeSubject}` : ''}
            </p>

            {studentsInSubject.length === 0 ? (
              <p className="text-sm text-muted">
                {t('analytics.noStudentData')}
              </p>
            ) : (
              <>
                <div className="form-group mb-20" style={{ maxWidth: 280 }}>
                  <label className="form-label">{t('analytics.studentLabel')}</label>
                  <select
                    className="form-select"
                    value={currentStudent?.studentId ?? ''}
                    onChange={(e) =>
                      setSelectedStudentId(Number(e.target.value))
                    }
                  >
                    {studentsInSubject.map((s) => (
                      <option key={s.studentId} value={s.studentId}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                {currentStudent && (
                  <div className="grid-2 gap-24">
                    {/* Student trendline */}
                    <div className="card card-padding">
                      <div className="flex items-center gap-8 mb-16">
                        <h4 className="text-sm font-semibold">
                          {currentStudent.name} \u2014 {t('analytics.scoreTrendSuffix')}
                        </h4>
                        <TrendBadge
                          data={currentStudent.scores.map((s) => s.pct)}
                        />
                      </div>

                      <div className="analytics-chart-container">
                        <TrendLine
                          data={currentStudent.scores.map((s) => s.pct)}
                          overlayData={classAvgs.slice(
                            0,
                            currentStudent.scores.length,
                          )}
                          width={440}
                          height={200}
                        />
                        <div className="analytics-chart-labels">
                          {currentStudent.scores.map((s, i) => (
                            <span key={i} className="analytics-chart-xlabel">
                              {s.exam}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="flex items-center gap-16 mt-12">
                        <span className="analytics-legend">
                          <span
                            className="analytics-legend-line"
                            style={{ background: 'var(--accent-copper)' }}
                          />
                          {t('analytics.studentLegend')}
                        </span>
                        <span className="analytics-legend">
                          <span className="analytics-legend-line analytics-legend-dashed" />
                          {t('analytics.classAvgLegend')}
                        </span>
                      </div>
                    </div>

                    {/* Topic breakdown */}
                    <div className="card card-padding">
                      <h4 className="text-sm font-semibold mb-16">
                        {currentStudent.name} \u2014 {t('analytics.topicDistSuffix')}
                      </h4>
                      {topicData.length > 0 ? (
                        <div className="flex flex-col gap-10">
                          {topicData.map((topic) => (
                            <HorizontalBar
                              key={topic.topic}
                              label={topic.topic}
                              pct={topic.pct}
                            />
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted">
                          {t('analytics.noTopicData')}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </section>
        </>
      )}
    </div>
  )
}
